import {
  ArrowLeft,
  BookOpen,
  Calendar,
  CheckCircle2,
  Contact,
  CreditCard,
  Database,
  Edit,
  HardDrive,
  Linkedin,
  Loader2,
  Mail,
  MessageSquare,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { openGmailAuthDialog } from '@/lib/gmail-auth-dialog'
import { openGoogleConnectorAuthDialog } from '@/lib/google-connector-auth-dialog'
import { useConnectors } from '@/lib/hooks/use-connectors'
import { openLinkedInAuthDialog } from '@/lib/linkedin-auth-dialog'
import type { Connector, ConnectorType } from '@/lib/types'

interface ConnectorTypeDetailProps {
  type: ConnectorType
  onBack: () => void
}

const TYPE_INFO: Record<
  ConnectorType,
  { name: string; icon: React.ReactNode }
> = {
  postgres: { name: 'Database', icon: <Database className="size-4" /> },
  mysql: { name: 'Database', icon: <Database className="size-4" /> },
  resend: { name: 'Email', icon: <Mail className="size-4" /> },
  slack: { name: 'Slack', icon: <MessageSquare className="size-4" /> },
  stripe: { name: 'Stripe', icon: <CreditCard className="size-4" /> },
  gmail: { name: 'Gmail', icon: <Mail className="size-4" /> },
  linkedin: { name: 'LinkedIn', icon: <Linkedin className="size-4" /> },
  google_calendar: {
    name: 'Google Calendar',
    icon: <Calendar className="size-4" />,
  },
  google_drive: {
    name: 'Google Drive',
    icon: <HardDrive className="size-4" />,
  },
  google_docs: { name: 'Google Docs', icon: <BookOpen className="size-4" /> },
  google_contacts: {
    name: 'Google Contacts',
    icon: <Contact className="size-4" />,
  },
}

export function ConnectorTypeDetail({
  type,
  onBack,
}: ConnectorTypeDetailProps) {
  const {
    connectors,
    isLoading,
    refetch,
    createConnector,
    updateConnector,
    deleteConnector,
    testConnector,
  } = useConnectors()

  const [isAddingNew, setIsAddingNew] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [updateError, setUpdateError] = useState<string | null>(null)
  const [testingId, setTestingId] = useState<string | null>(null)
  const [testResults, setTestResults] = useState<
    Record<string, { success: boolean; error?: string }>
  >({})
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [isConnectingGmail, setIsConnectingGmail] = useState(false)
  const [gmailError, setGmailError] = useState<string | null>(null)
  const [gmailPermission, setGmailPermission] = useState<'read' | 'read_send'>(
    'read'
  )
  const [isConnectingLinkedIn, setIsConnectingLinkedIn] = useState(false)
  const [linkedinError, setLinkedinError] = useState<string | null>(null)
  const [linkedinPermission, setLinkedinPermission] = useState<
    'profile' | 'profile_post'
  >('profile')
  const [isConnectingGoogleWs, setIsConnectingGoogleWs] = useState(false)
  const [googleWsError, setGoogleWsError] = useState<string | null>(null)
  const [googleWsPermission, setGoogleWsPermission] = useState<
    'read' | 'read_write'
  >('read')
  const [isChangingPermission, setIsChangingPermission] = useState(false)

  // Form state for new connector
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    port: '5432',
    database: '',
    user: '',
    password: '',
    ssl: true,
    apiKey: '',
    from: '',
    replyTo: '',
    botToken: '',
    channel: '',
    stripeApiKey: '',
  })

  const GOOGLE_WORKSPACE_TYPES: ConnectorType[] = [
    'google_calendar',
    'google_drive',
    'google_docs',
    'google_contacts',
  ]
  const isGoogleWorkspaceType = GOOGLE_WORKSPACE_TYPES.includes(type)

  // Filter connectors by type
  const typeConnectors = connectors.filter((c) => c.type === type)
  const typeInfo = TYPE_INFO[type] || {
    name: type,
    icon: <Database className="size-4" />,
  }

  const resetForm = () => {
    setFormData({
      name: '',
      host: '',
      port: '5432',
      database: '',
      user: '',
      password: '',
      ssl: true,
      apiKey: '',
      from: '',
      replyTo: '',
      botToken: '',
      channel: '',
      stripeApiKey: '',
    })
    setCreateError(null)
    setUpdateError(null)
  }

  const loadConnectorIntoForm = (connector: Connector) => {
    if (type === 'stripe') {
      setFormData({
        name: connector.name,
        host: '',
        port: '5432',
        database: '',
        user: '',
        password: '',
        ssl: true,
        apiKey: '',
        from: '',
        replyTo: '',
        botToken: '',
        channel: '',
        stripeApiKey: '', // Credentials are not returned from backend
      })
    } else if (type === 'resend') {
      const config = connector.config as {
        from?: string
        replyTo?: string
      }
      setFormData({
        name: connector.name,
        host: '',
        port: '5432',
        database: '',
        user: '',
        password: '', // Credentials are not returned from backend
        ssl: true,
        apiKey: '', // Credentials are not returned from backend
        from: config.from || '',
        replyTo: config.replyTo || '',
        botToken: '',
        channel: '',
        stripeApiKey: '',
      })
    } else if (type === 'slack') {
      const config = connector.config as {
        channel?: string
      }
      setFormData({
        name: connector.name,
        host: '',
        port: '5432',
        database: '',
        user: '',
        password: '',
        ssl: true,
        apiKey: '',
        from: '',
        replyTo: '',
        botToken: '', // Credentials are not returned from backend
        channel: config.channel || '',
        stripeApiKey: '',
      })
    } else {
      const config = connector.config as {
        host?: string
        port?: number
        database?: string
        ssl?: boolean
      }
      setFormData({
        name: connector.name,
        host: config.host || '',
        port: String(config.port || 5432),
        database: config.database || '',
        user: '', // Credentials are not returned from backend
        password: '', // Credentials are not returned from backend
        ssl: config.ssl ?? true,
        apiKey: '',
        from: '',
        replyTo: '',
        botToken: '',
        channel: '',
        stripeApiKey: '',
      })
    }
  }

  const handleCreate = async () => {
    if (type === 'stripe') {
      if (!formData.name || !formData.stripeApiKey) {
        setCreateError('Please fill in all required fields')
        return
      }
    } else if (type === 'resend') {
      if (!formData.name || !formData.apiKey) {
        setCreateError('Please fill in all required fields')
        return
      }
    } else if (type === 'slack') {
      if (!formData.name || !formData.botToken) {
        setCreateError('Please fill in all required fields')
        return
      }
    } else {
      if (
        !formData.name ||
        !formData.host ||
        !formData.database ||
        !formData.user
      ) {
        setCreateError('Please fill in all required fields')
        return
      }
    }

    setIsCreating(true)
    setCreateError(null)

    try {
      if (type === 'stripe') {
        const mode = formData.stripeApiKey.startsWith('sk_test_')
          ? 'test'
          : formData.stripeApiKey.startsWith('sk_live_')
            ? 'live'
            : 'unknown'
        await createConnector(
          type,
          formData.name.trim(),
          { mode },
          { apiKey: formData.stripeApiKey }
        )
      } else if (type === 'resend') {
        await createConnector(
          type,
          formData.name.trim(),
          {
            from: formData.from.trim() || undefined,
            replyTo: formData.replyTo.trim() || undefined,
          },
          {
            apiKey: formData.apiKey,
          }
        )
      } else if (type === 'slack') {
        await createConnector(
          type,
          formData.name.trim(),
          {
            channel: formData.channel.trim() || undefined,
          },
          {
            botToken: formData.botToken,
          }
        )
      } else {
        await createConnector(
          type,
          formData.name.trim(),
          {
            host: formData.host.trim(),
            port: Number(formData.port) || 5432,
            database: formData.database.trim(),
            ssl: formData.ssl,
          },
          {
            user: formData.user.trim(),
            password: formData.password,
          }
        )
      }
      setIsAddingNew(false)
      resetForm()
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to create connector'
      )
    } finally {
      setIsCreating(false)
    }
  }

  const handleTest = async (id: string) => {
    setTestingId(id)
    setTestResults((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })

    const result = await testConnector(id)
    setTestResults((prev) => ({ ...prev, [id]: result }))
    setTestingId(null)
  }

  const handleUpdate = async () => {
    if (!editingId) return

    if (type === 'stripe') {
      if (!formData.name) {
        setUpdateError('Please fill in all required fields')
        return
      }
    } else if (type === 'resend') {
      if (!formData.name) {
        setUpdateError('Please fill in all required fields')
        return
      }
    } else if (type === 'slack') {
      if (!formData.name) {
        setUpdateError('Please fill in all required fields')
        return
      }
    } else {
      if (!formData.name || !formData.host || !formData.database) {
        setUpdateError('Please fill in all required fields')
        return
      }

      // If password is provided, username is also required
      if (formData.password && !formData.user) {
        setUpdateError('Username is required when updating password')
        return
      }
    }

    setIsUpdating(true)
    setUpdateError(null)

    try {
      if (type === 'stripe') {
        const credentials = formData.stripeApiKey
          ? { apiKey: formData.stripeApiKey }
          : undefined
        const mode = formData.stripeApiKey
          ? formData.stripeApiKey.startsWith('sk_test_')
            ? 'test'
            : formData.stripeApiKey.startsWith('sk_live_')
              ? 'live'
              : 'unknown'
          : undefined
        await updateConnector(
          editingId,
          formData.name.trim(),
          mode ? { mode } : {},
          credentials
        )
      } else if (type === 'resend') {
        // Only include credentials if API key is provided (user wants to update it)
        const credentials = formData.apiKey
          ? {
              apiKey: formData.apiKey,
            }
          : undefined

        await updateConnector(
          editingId,
          formData.name.trim(),
          {
            from: formData.from.trim() || undefined,
            replyTo: formData.replyTo.trim() || undefined,
          },
          credentials
        )
      } else if (type === 'slack') {
        // Only include credentials if bot token is provided (user wants to update it)
        const credentials = formData.botToken
          ? {
              botToken: formData.botToken,
            }
          : undefined

        await updateConnector(
          editingId,
          formData.name.trim(),
          {
            channel: formData.channel.trim() || undefined,
          },
          credentials
        )
      } else {
        const config = {
          host: formData.host.trim(),
          port: Number(formData.port) || 5432,
          database: formData.database.trim(),
          ssl: formData.ssl,
        }

        // Only include credentials if password is provided (user wants to update it)
        const credentials = formData.password
          ? {
              user: formData.user.trim(),
              password: formData.password,
            }
          : undefined

        await updateConnector(
          editingId,
          formData.name.trim(),
          config,
          credentials
        )
      }
      setEditingId(null)
      resetForm()
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : 'Failed to update connector'
      )
    } finally {
      setIsUpdating(false)
    }
  }

  const handleEdit = (connector: Connector) => {
    loadConnectorIntoForm(connector)
    setEditingId(connector.id)
    setIsAddingNew(false)
  }

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    try {
      await deleteConnector(id)
    } catch (err) {
      console.error('Failed to delete connector:', err)
    } finally {
      setDeletingId(null)
    }
  }

  const handleConnectGmail = async (
    permission: 'read' | 'read_send' = gmailPermission
  ) => {
    setIsConnectingGmail(true)
    setGmailError(null)

    try {
      const result = await openGmailAuthDialog(permission)
      if (result?.success && result?.email) {
        await refetch()
      } else {
        setGmailError('Failed to connect Gmail')
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to connect Gmail'
      setGmailError(message)
    } finally {
      setIsConnectingGmail(false)
    }
  }

  const handleChangeGmailPermission = async (
    newPermission: 'read' | 'read_send'
  ) => {
    setIsChangingPermission(true)
    setGmailError(null)

    try {
      // Delete existing connector first
      const gmailConnector = typeConnectors[0]
      if (gmailConnector) {
        await deleteConnector(gmailConnector.id)
      }
      // Re-auth with new permission
      await handleConnectGmail(newPermission)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to change permission'
      setGmailError(message)
    } finally {
      setIsChangingPermission(false)
    }
  }

  const handleConnectLinkedIn = async (
    permission: 'profile' | 'profile_post' = linkedinPermission
  ) => {
    setIsConnectingLinkedIn(true)
    setLinkedinError(null)

    try {
      const result = await openLinkedInAuthDialog(permission)
      if (result?.success && result?.name) {
        await refetch()
      } else {
        setLinkedinError('Failed to connect LinkedIn')
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to connect LinkedIn'
      setLinkedinError(message)
    } finally {
      setIsConnectingLinkedIn(false)
    }
  }

  const handleChangeLinkedInPermission = async (
    newPermission: 'profile' | 'profile_post'
  ) => {
    setIsChangingPermission(true)
    setLinkedinError(null)

    try {
      const linkedinConnector = typeConnectors[0]
      if (linkedinConnector) {
        await deleteConnector(linkedinConnector.id)
      }
      await handleConnectLinkedIn(newPermission)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to change permission'
      setLinkedinError(message)
    } finally {
      setIsChangingPermission(false)
    }
  }

  const handleConnectGoogleWorkspace = async (
    permission: 'read' | 'read_write' = googleWsPermission
  ) => {
    setIsConnectingGoogleWs(true)
    setGoogleWsError(null)

    try {
      const result = await openGoogleConnectorAuthDialog(
        type as
          | 'google_calendar'
          | 'google_drive'
          | 'google_docs'
          | 'google_contacts',
        permission
      )
      if (result?.success && result?.email) {
        await refetch()
      } else {
        setGoogleWsError(`Failed to connect ${typeInfo.name}`)
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : `Failed to connect ${typeInfo.name}`
      setGoogleWsError(message)
    } finally {
      setIsConnectingGoogleWs(false)
    }
  }

  const handleChangeGoogleWorkspacePermission = async (
    newPermission: 'read' | 'read_write'
  ) => {
    setIsChangingPermission(true)
    setGoogleWsError(null)

    try {
      const existing = typeConnectors[0]
      if (existing) {
        await deleteConnector(existing.id)
      }
      await handleConnectGoogleWorkspace(newPermission)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to change permission'
      setGoogleWsError(message)
    } finally {
      setIsChangingPermission(false)
    }
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b">
        <Button variant="ghost" size="icon" onClick={onBack} className="size-8">
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-2">
          {typeInfo.icon}
          <h1 className="text-sm font-semibold">{typeInfo.name} Connections</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Add button or Gmail Connect button */}
        {!isAddingNew && !editingId && (
          <>
            {type === 'gmail' ? (
              <>
                {typeConnectors.length === 0 && (
                  <div className="space-y-3 mb-4">
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">
                        Permission level
                      </p>
                      <label className="flex items-start gap-2 p-2 rounded-md border cursor-pointer hover:bg-muted/50">
                        <input
                          type="radio"
                          name="gmail-permission"
                          value="read"
                          checked={gmailPermission === 'read'}
                          onChange={() => setGmailPermission('read')}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="text-sm font-medium">Read only</p>
                          <p className="text-xs text-muted-foreground">
                            Search and read emails. Cannot send.
                          </p>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 p-2 rounded-md border cursor-pointer hover:bg-muted/50">
                        <input
                          type="radio"
                          name="gmail-permission"
                          value="read_send"
                          checked={gmailPermission === 'read_send'}
                          onChange={() => setGmailPermission('read_send')}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="text-sm font-medium">Read & Send</p>
                          <p className="text-xs text-muted-foreground">
                            Search, read, and send emails on your behalf.
                          </p>
                        </div>
                      </label>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleConnectGmail()}
                      disabled={isConnectingGmail}
                      className="w-full"
                    >
                      {isConnectingGmail ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <Plus className="size-4 mr-2" />
                          Connect Gmail
                        </>
                      )}
                    </Button>
                  </div>
                )}
                {gmailError && (
                  <p className="text-xs text-destructive mb-4">{gmailError}</p>
                )}
              </>
            ) : type === 'linkedin' ? (
              <>
                {typeConnectors.length === 0 && (
                  <div className="space-y-3 mb-4">
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">
                        Permission level
                      </p>
                      <label className="flex items-start gap-2 p-2 rounded-md border cursor-pointer hover:bg-muted/50">
                        <input
                          type="radio"
                          name="linkedin-permission"
                          value="profile"
                          checked={linkedinPermission === 'profile'}
                          onChange={() => setLinkedinPermission('profile')}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="text-sm font-medium">Profile only</p>
                          <p className="text-xs text-muted-foreground">
                            Identify your LinkedIn account. Cannot post.
                          </p>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 p-2 rounded-md border cursor-pointer hover:bg-muted/50">
                        <input
                          type="radio"
                          name="linkedin-permission"
                          value="profile_post"
                          checked={linkedinPermission === 'profile_post'}
                          onChange={() => setLinkedinPermission('profile_post')}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="text-sm font-medium">Profile + Post</p>
                          <p className="text-xs text-muted-foreground">
                            Identify your account and create posts on your
                            behalf.
                          </p>
                        </div>
                      </label>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleConnectLinkedIn()}
                      disabled={isConnectingLinkedIn}
                      className="w-full"
                    >
                      {isConnectingLinkedIn ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <Plus className="size-4 mr-2" />
                          Connect LinkedIn
                        </>
                      )}
                    </Button>
                  </div>
                )}
                {linkedinError && (
                  <p className="text-xs text-destructive mb-4">
                    {linkedinError}
                  </p>
                )}
              </>
            ) : isGoogleWorkspaceType ? (
              <>
                {typeConnectors.length === 0 && (
                  <div className="space-y-3 mb-4">
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">
                        Permission level
                      </p>
                      <label className="flex items-start gap-2 p-2 rounded-md border cursor-pointer hover:bg-muted/50">
                        <input
                          type="radio"
                          name="gws-permission"
                          value="read"
                          checked={googleWsPermission === 'read'}
                          onChange={() => setGoogleWsPermission('read')}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="text-sm font-medium">Read only</p>
                          <p className="text-xs text-muted-foreground">
                            View and search data. Cannot create or modify.
                          </p>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 p-2 rounded-md border cursor-pointer hover:bg-muted/50">
                        <input
                          type="radio"
                          name="gws-permission"
                          value="read_write"
                          checked={googleWsPermission === 'read_write'}
                          onChange={() => setGoogleWsPermission('read_write')}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="text-sm font-medium">Read & Write</p>
                          <p className="text-xs text-muted-foreground">
                            View, create, and modify data on your behalf.
                          </p>
                        </div>
                      </label>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleConnectGoogleWorkspace()}
                      disabled={isConnectingGoogleWs}
                      className="w-full"
                    >
                      {isConnectingGoogleWs ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <Plus className="size-4 mr-2" />
                          Connect {typeInfo.name}
                        </>
                      )}
                    </Button>
                  </div>
                )}
                {googleWsError && (
                  <p className="text-xs text-destructive mb-4">
                    {googleWsError}
                  </p>
                )}
              </>
            ) : (
              <Button
                size="sm"
                onClick={() => setIsAddingNew(true)}
                className="w-full mb-4"
              >
                <Plus className="size-4 mr-2" />
                Add Connection
              </Button>
            )}
          </>
        )}

        {/* Add form (not for OAuth connectors) */}
        {isAddingNew &&
          type !== 'gmail' &&
          type !== 'linkedin' &&
          !isGoogleWorkspaceType && (
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30 mb-4">
              <p className="text-xs font-medium text-muted-foreground">
                New {typeInfo.name} Connection
              </p>
              <Input
                placeholder={
                  type === 'stripe'
                    ? "Connection name (e.g., 'My Stripe')"
                    : type === 'resend'
                      ? "Connection name (e.g., 'Production Email')"
                      : "Connection name (e.g., 'Production DB')"
                }
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="h-9 text-sm"
              />
              {type === 'stripe' ? (
                <>
                  <Input
                    type="password"
                    placeholder="Stripe Secret Key (sk_test_... or sk_live_...)"
                    value={formData.stripeApiKey}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        stripeApiKey: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  {formData.stripeApiKey && (
                    <p className="text-xs text-muted-foreground">
                      Mode:{' '}
                      {formData.stripeApiKey.startsWith('sk_test_') ? (
                        <span className="text-amber-600 font-medium">Test</span>
                      ) : formData.stripeApiKey.startsWith('sk_live_') ? (
                        <span className="text-green-600 font-medium">Live</span>
                      ) : (
                        <span className="text-muted-foreground">
                          Unknown key format
                        </span>
                      )}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Use a restricted key with read-only access from your Stripe
                    Dashboard
                  </p>
                </>
              ) : type === 'resend' ? (
                <>
                  <Input
                    type="password"
                    placeholder="Resend API Key"
                    value={formData.apiKey}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        apiKey: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <Input
                    type="email"
                    placeholder="Sender email address (e.g., noreply@example.com)"
                    value={formData.from}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        from: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <Input
                    type="email"
                    placeholder="Reply-to email address (optional)"
                    value={formData.replyTo}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        replyTo: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                </>
              ) : type === 'slack' ? (
                <>
                  <Input
                    type="password"
                    placeholder="Slack Bot Token (xoxb-...)"
                    value={formData.botToken}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        botToken: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <Input
                    placeholder="Default channel (e.g., #general) - optional"
                    value={formData.channel}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        channel: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Channel can be specified per message if not set here
                  </p>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Host"
                      value={formData.host}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          host: e.target.value,
                        }))
                      }
                      className="h-9 text-sm col-span-2"
                    />
                    <Input
                      placeholder="Port"
                      value={formData.port}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          port: e.target.value,
                        }))
                      }
                      className="h-9 text-sm"
                    />
                  </div>
                  <Input
                    placeholder="Database name"
                    value={formData.database}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        database: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Username"
                      value={formData.user}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          user: e.target.value,
                        }))
                      }
                      className="h-9 text-sm"
                    />
                    <Input
                      type="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      className="h-9 text-sm"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={formData.ssl}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          ssl: e.target.checked,
                        }))
                      }
                      className="rounded"
                    />
                    Use SSL
                  </label>
                </>
              )}

              {createError && (
                <p className="text-xs text-destructive">{createError}</p>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={isCreating}
                  className="flex-1"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="size-3 mr-1 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Connection'
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsAddingNew(false)
                    resetForm()
                  }}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

        {/* Edit form (not for OAuth connectors) */}
        {editingId &&
          type !== 'gmail' &&
          type !== 'linkedin' &&
          !isGoogleWorkspaceType && (
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30 mb-4">
              <p className="text-xs font-medium text-muted-foreground">
                Edit {typeInfo.name} Connection
              </p>
              <Input
                placeholder={
                  type === 'resend'
                    ? "Connection name (e.g., 'Production Email')"
                    : type === 'slack'
                      ? "Connection name (e.g., 'Team Slack')"
                      : "Connection name (e.g., 'Production DB')"
                }
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="h-9 text-sm"
              />
              {type === 'stripe' ? (
                <>
                  <Input
                    type="password"
                    placeholder="Stripe Secret Key (leave empty to keep current)"
                    value={formData.stripeApiKey}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        stripeApiKey: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave API key empty to keep current credentials unchanged
                  </p>
                </>
              ) : type === 'resend' ? (
                <>
                  <Input
                    type="password"
                    placeholder="Resend API Key (leave empty to keep current)"
                    value={formData.apiKey}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        apiKey: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <Input
                    type="email"
                    placeholder="Sender email address (e.g., noreply@example.com)"
                    value={formData.from}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        from: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <Input
                    type="email"
                    placeholder="Reply-to email address (optional)"
                    value={formData.replyTo}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        replyTo: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave API key empty to keep current credentials unchanged
                  </p>
                </>
              ) : type === 'slack' ? (
                <>
                  <Input
                    type="password"
                    placeholder="Slack Bot Token (leave empty to keep current)"
                    value={formData.botToken}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        botToken: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <Input
                    placeholder="Default channel (e.g., #general) - optional"
                    value={formData.channel}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        channel: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave bot token empty to keep current credentials unchanged.
                    Channel can be specified per message if not set here.
                  </p>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Host"
                      value={formData.host}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          host: e.target.value,
                        }))
                      }
                      className="h-9 text-sm col-span-2"
                    />
                    <Input
                      placeholder="Port"
                      value={formData.port}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          port: e.target.value,
                        }))
                      }
                      className="h-9 text-sm"
                    />
                  </div>
                  <Input
                    placeholder="Database name"
                    value={formData.database}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        database: e.target.value,
                      }))
                    }
                    className="h-9 text-sm"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Username (required if updating password)"
                      value={formData.user}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          user: e.target.value,
                        }))
                      }
                      className="h-9 text-sm"
                    />
                    <Input
                      type="password"
                      placeholder="Password (leave empty to keep current)"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      className="h-9 text-sm"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave password empty to keep current credentials unchanged
                  </p>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={formData.ssl}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          ssl: e.target.checked,
                        }))
                      }
                      className="rounded"
                    />
                    Use SSL
                  </label>
                </>
              )}

              {updateError && (
                <p className="text-xs text-destructive">{updateError}</p>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  className="flex-1"
                >
                  {isUpdating ? (
                    <>
                      <Loader2 className="size-3 mr-1 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingId(null)
                    resetForm()
                  }}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

        {/* Connections list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : typeConnectors.length === 0 ? (
          <div className="text-center py-8">
            {typeInfo.icon && (
              <div className="text-muted-foreground/50 mx-auto mb-2 flex justify-center">
                {typeInfo.icon}
              </div>
            )}
            <p className="text-sm text-muted-foreground">No connections yet</p>
            <p className="text-xs text-muted-foreground">
              Add a connection to get started
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {typeConnectors.map((connector) => {
              const testResult = testResults[connector.id]
              const isTesting = testingId === connector.id
              const isDeleting = deletingId === connector.id

              return (
                <div
                  key={connector.id}
                  className="flex flex-col gap-2 p-3 bg-muted/50 rounded-lg border border-border/50"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {connector.name}
                      </p>
                      {type === 'stripe' ? (
                        <p className="text-xs text-muted-foreground truncate">
                          Stripe API{' '}
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-background border ${
                              (connector.config as { mode?: string })?.mode ===
                              'test'
                                ? 'text-amber-600'
                                : 'text-green-600'
                            }`}
                          >
                            {(connector.config as { mode?: string })?.mode ===
                            'test'
                              ? 'Test'
                              : 'Live'}
                          </span>
                        </p>
                      ) : type === 'resend' ? (
                        <p className="text-xs text-muted-foreground truncate">
                          {(connector.config as { from?: string })?.from ||
                            'Resend API'}
                        </p>
                      ) : type === 'slack' ? (
                        <p className="text-xs text-muted-foreground truncate">
                          {(connector.config as { channel?: string })
                            ?.channel || 'Slack Bot'}
                        </p>
                      ) : type === 'gmail' ? (
                        <p className="text-xs text-muted-foreground truncate">
                          {(connector.config as { email?: string })?.email ||
                            ''}{' '}
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-background border">
                            {(connector.config as { permission?: string })
                              ?.permission === 'read_send'
                              ? 'Read & Send'
                              : 'Read only'}
                          </span>
                        </p>
                      ) : type === 'linkedin' ? (
                        <p className="text-xs text-muted-foreground truncate">
                          {(connector.config as { email?: string })?.email ||
                            ''}{' '}
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-background border">
                            {(connector.config as { permission?: string })
                              ?.permission === 'profile_post'
                              ? 'Profile + Post'
                              : 'Profile only'}
                          </span>
                        </p>
                      ) : isGoogleWorkspaceType ? (
                        <p className="text-xs text-muted-foreground truncate">
                          {(connector.config as { email?: string })?.email ||
                            ''}{' '}
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-background border">
                            {(connector.config as { permission?: string })
                              ?.permission === 'read_write'
                              ? 'Read & Write'
                              : 'Read only'}
                          </span>
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground truncate">
                          {String(connector.config.host || '')}
                          {connector.config.database
                            ? ` / ${connector.config.database}`
                            : ''}
                        </p>
                      )}
                      {testResult &&
                        !testResult.success &&
                        testResult.error && (
                          <p className="text-xs text-destructive mt-1 truncate">
                            {testResult.error}
                          </p>
                        )}
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {type !== 'gmail' &&
                        type !== 'linkedin' &&
                        !isGoogleWorkspaceType && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleTest(connector.id)}
                              disabled={
                                isTesting ||
                                isDeleting ||
                                editingId === connector.id
                              }
                              className="h-8 px-2"
                              title="Test connection"
                            >
                              {isTesting ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : testResult?.success ? (
                                <CheckCircle2 className="size-4 text-green-500" />
                              ) : testResult && !testResult.success ? (
                                <XCircle className="size-4 text-destructive" />
                              ) : (
                                'Test'
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(connector)}
                              disabled={
                                isDeleting ||
                                isTesting ||
                                editingId === connector.id
                              }
                              className="h-8 px-2"
                              title="Edit connection"
                            >
                              <Edit className="size-4" />
                            </Button>
                          </>
                        )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(connector.id)}
                        disabled={
                          isDeleting ||
                          isTesting ||
                          editingId === connector.id ||
                          isChangingPermission
                        }
                        className="h-8 px-2"
                        title={
                          type === 'gmail' || isGoogleWorkspaceType
                            ? `Disconnect ${typeInfo.name}`
                            : 'Delete connection'
                        }
                      >
                        {isDeleting ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Trash2 className="size-4 text-destructive" />
                        )}
                      </Button>
                    </div>
                  </div>
                  {type === 'gmail' && (
                    <div className="flex gap-2">
                      {(connector.config as { permission?: string })
                        ?.permission !== 'read_send' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleChangeGmailPermission('read_send')
                          }
                          disabled={isChangingPermission || isDeleting}
                          className="text-xs h-7"
                        >
                          {isChangingPermission ? (
                            <>
                              <Loader2 className="size-3 mr-1 animate-spin" />
                              Upgrading...
                            </>
                          ) : (
                            'Enable Send'
                          )}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChangeGmailPermission('read')}
                          disabled={isChangingPermission || isDeleting}
                          className="text-xs h-7"
                        >
                          {isChangingPermission ? (
                            <>
                              <Loader2 className="size-3 mr-1 animate-spin" />
                              Downgrading...
                            </>
                          ) : (
                            'Read Only'
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                  {type === 'linkedin' && (
                    <div className="flex gap-2">
                      {(connector.config as { permission?: string })
                        ?.permission !== 'profile_post' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleChangeLinkedInPermission('profile_post')
                          }
                          disabled={isChangingPermission || isDeleting}
                          className="text-xs h-7"
                        >
                          {isChangingPermission ? (
                            <>
                              <Loader2 className="size-3 mr-1 animate-spin" />
                              Upgrading...
                            </>
                          ) : (
                            'Enable Posting'
                          )}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleChangeLinkedInPermission('profile')
                          }
                          disabled={isChangingPermission || isDeleting}
                          className="text-xs h-7"
                        >
                          {isChangingPermission ? (
                            <>
                              <Loader2 className="size-3 mr-1 animate-spin" />
                              Downgrading...
                            </>
                          ) : (
                            'Profile Only'
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                  {isGoogleWorkspaceType && (
                    <div className="flex gap-2">
                      {(connector.config as { permission?: string })
                        ?.permission !== 'read_write' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleChangeGoogleWorkspacePermission('read_write')
                          }
                          disabled={isChangingPermission || isDeleting}
                          className="text-xs h-7"
                        >
                          {isChangingPermission ? (
                            <>
                              <Loader2 className="size-3 mr-1 animate-spin" />
                              Upgrading...
                            </>
                          ) : (
                            'Enable Write'
                          )}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleChangeGoogleWorkspacePermission('read')
                          }
                          disabled={isChangingPermission || isDeleting}
                          className="text-xs h-7"
                        >
                          {isChangingPermission ? (
                            <>
                              <Loader2 className="size-3 mr-1 animate-spin" />
                              Downgrading...
                            </>
                          ) : (
                            'Read Only'
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
